//------ 草图绘制转状态 开始------
import {isSketchContour, isSketchPointObject} from '../../mesh';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    InteractionVisualStateContext,
    InteractionVisualStateRuntime,
    MaterialApplySource,
} from './AbstractInteractionVisualState';
import type {Viewer} from '../../viewer/Viewer';

/**
 * 草图绘制转状态。
 * 说明：
 * 1. 直接继承 AbstractInteractionVisualState，在绘制模式下精简视觉刷新逻辑；
 * 2. 保留线/边悬浮高亮和关联端点高亮，保留点悬浮高亮（受捕捉守卫保护）；
 * 3. 不处理轮廓高亮（移入轮廓不高亮关联边）和选择状态；
 * 4. 已选中新建实体继续显示已有关系标签，保证绘制完成反馈与普通草图编辑一致。
 */
export class SketchDrawingTransitionState extends AbstractInteractionVisualState {
    /**
     * 构造函数。
     * @param runtime 状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
    }

    /**
     * 刷新视觉状态（绘制模式专用）。
     * 说明：
     * 1. 恢复所有接管材质并清空状态机；
     * 2. 重新应用外部状态提示；
     * 3. 对非轮廓悬浮对象应用悬浮高亮，对线段同时高亮关联端点；
     * 4. 点对象悬浮受捕捉高亮守卫保护；
     * 5. 不处理选中对象和轮廓关联边高亮；
     * 6. 同步已选中新建实体的关系标签，不引入轮廓或选择材质联动。
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        // 恢复所有接管材质。
        this.restoreAllStyledMaterials();
        // 清空状态机。
        this.runtime.objectStateMachine.clear();
        // 重新铺设默认态材质，保证后续 Hover/手动状态在统一基础态上叠加。
        this.applyDefaultMaterialsToSketchObjects(context);
        // 清空标签视觉状态。
        context.relationLabelState.clearMarkerVisualStates();

        // 重新应用外部状态提示（与父类逻辑一致）。
        this.runtime.externalStateHints.forEach((hintState, objectUuid) => {
            // 获取原始材质快照。
            const materialSnapshot = this.runtime.originalMaterialSnapshots.get(objectUuid);
            if (!materialSnapshot) {
                return;
            }
            // 应用外部状态。
            this.setObjectState(
                materialSnapshot.object,
                hintState,
                MaterialApplySource.Direct,
                context,
            );
        });

        // 重新应用外部手动状态提示。
        this.applyManualStateHints(context);

        // 处理悬浮对象：非轮廓对象可悬浮高亮，点对象受捕捉守卫保护。
        if (context.hoveredObject) {
            // 对点对象应用捕捉高亮守卫。
            const allowHover = this.canHoverPointInDrawingMode(context.hoveredObject, context);

            if (allowHover) {
                // 非轮廓对象（线段、圆弧、圆、椭圆、点等）直接高亮。
                if (!isSketchContour(context.hoveredObject)) {
                    this.setObjectState(
                        context.hoveredObject,
                        GeometryInteractionState.Hover,
                        MaterialApplySource.Direct,
                        context,
                    );
                }

                // 对悬浮线类对象的关联控制点应用联动高亮（传入空选中集合，绘制模式无选择）。
                // applyLineControlPointHoverState 内部处理 SketchLineObject / SketchSplineObject，
                // 其他对象类型不会有副作用。
                const emptySelectedSet = new Set<string>();
                this.applyLineControlPointHoverState(context, context.hoveredObject, emptySelectedSet);
            }
        }

        // 绘制完成后工具仍保持激活；必须同步程序化选中新实体的已有关系标签。
        this.syncSelectedObjectConstraintMarkers(context);
    }

}
//------ 草图绘制转状态 结束------
